<script setup lang="ts">
export interface AuthProviderButtons {
  disabled?: boolean;
}

const props = defineProps<AuthProviderButtons>();
const emit = defineEmits(["clickProvider"]);
const { t } = useI18n();
const appConfig = useRuntimeConfig().public;

const hasAppleAuth = appConfig.APP_CUSTOMER.app_config.enable_auth_via_apple && appConfig.APP_CUSTOMER.app_config.app_client_id_for_apple;
const hasGoogleAuth = appConfig.APP_CUSTOMER.app_config.enable_auth_via_google && appConfig.APP_CUSTOMER.app_config.app_client_id_for_google;
const hasFacebookAuth = appConfig.APP_CUSTOMER.app_config.enable_auth_via_facebook && appConfig.APP_CUSTOMER.app_config.app_id_facebook;
const hasIfaAuth = appConfig.APP_FLAGS.hasIfaAuth;
const hasSocialAuth = hasAppleAuth || hasGoogleAuth || hasFacebookAuth || hasIfaAuth;
const onAuthProviderClick = (provider: string) => emit("clickProvider", provider);
</script>

<template>
  <div v-if="hasSocialAuth" class="mt-8">
    <div class="flex items-center mb-6">
      <div class="hr w-full" />
      <div class="text-secondary text-xs mx-2">
        {{ t('labels.or') }}
      </div>
      <div class="hr w-full" />
    </div>

    <div>
      <BaseButton
        v-if="hasIfaAuth"
        style="--base-primary-accent-color: #2c374b"
        class="auth-button text-button-bold"
        name="ifa"
        full-width
        :disabled="props.disabled"
        @click="onAuthProviderClick('ifa')"
      >
        <BaseImage
          class="absolute top-2.5 left-3" src="/images/icons/icon-ifa-logo.webp"
          fallback-src="/images/mock/logo.svg" alt="Logo" :width="24" :height="24"
        />
        {{ t('labels.continue_with', { provider: 'IFA' }) }}
      </BaseButton>

      <BaseButton
        v-if="hasAppleAuth"
        style="--base-primary-accent-color: #000; color: #fff"
        class="auth-button text-button-bold dark:!text-[#000] dark:!bg-[#fff]"
        name="apple"
        full-width
        :disabled="props.disabled"
        @click="onAuthProviderClick('apple')"
      >
        <IconSocialApple class="absolute top-2.5 left-3 dark:!text-[#000]" />
        {{ t('labels.continue_with', { provider: 'Apple' }) }}
      </BaseButton>
      <BaseButton
        v-if="hasGoogleAuth"
        style="--base-primary-accent-color: #4285f4"
        class="auth-button text-button-bold"
        name="google"
        full-width
        :disabled="props.disabled"
        @click="onAuthProviderClick('google')"
      >
        <div class="absolute top-0 left-0 bg-white h-[46px] w-[46px] flex items-center justify-center rounded-sm">
          <IconSocialGoogle />
        </div>
        {{ t('labels.continue_with', { provider: 'Google' }) }}
      </BaseButton>
      <BaseButton
        v-if="hasFacebookAuth"
        style="--base-primary-accent-color: #3b5998"
        class="auth-button text-button-bold"
        name="facebook"
        full-width
        :disabled="props.disabled"
        @click="onAuthProviderClick('facebook')"
      >
        <IconSocialFacebook class="absolute top-2.5 left-3" />
        {{ t('labels.continue_with', { provider: 'Facebook' }) }}
      </BaseButton>
    </div>
  </div>
</template>

<style>
.auth-button {
  @apply relative my-2;
}
</style>
